import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Brain, StopCircle, ChevronDown } from 'lucide-react'
import type { TriggerRecordType, RootCauseAnalysisRequest } from '../types'
import { TRIGGER_RECORD_TYPE_LABELS } from '../types'
import { TriggerRecordList } from './TriggerRecordList'
import { waterQualityApi } from '@/features/water-quality/api/waterQualityApi'
import { fishHealthApi } from '@/api/fishHealthApi'
import { feedApi } from '@/api/feedApi'
import { maintenanceApi } from '@/api/maintenanceApi'
import type {
  WaterQualityResponse,
  FishHealthResponse,
  MaintenanceResponse,
  FeedRecordResponse,
} from '@/shared/types/api'

interface RootCauseFormProps {
  farmId: string
  ponds: Array<{ id: string; name: string }>
  isStreaming: boolean
  onSubmit: (request: RootCauseAnalysisRequest) => void
  onCancel: () => void
}

const RECORD_TYPES: Array<{ value: TriggerRecordType | ''; label: string }> = [
  { value: '', label: 'None (General Investigation)' },
  { value: 'water_quality', label: 'Water Quality Test' },
  { value: 'health_check', label: 'Health Check' },
  { value: 'feed_record', label: 'Feed Record' },
  { value: 'maintenance_check', label: 'Maintenance Check' },
]

type RecordData = WaterQualityResponse | FishHealthResponse | MaintenanceResponse | FeedRecordResponse

export function RootCauseForm({
  farmId,
  ponds,
  isStreaming,
  onSubmit,
  onCancel,
}: RootCauseFormProps) {
  const [pondId, setPondId] = useState('')
  const [isPondDropdownOpen, setIsPondDropdownOpen] = useState(false)
  const [recordType, setRecordType] = useState<TriggerRecordType | ''>('')
  const [selectedRecordId, setSelectedRecordId] = useState('')
  const [description, setDescription] = useState('')
  const [lookbackDays, setLookbackDays] = useState(30)

  const pondDropdownRef = useRef<HTMLDivElement>(null)
  const selectedPond = ponds.find(p => p.id === pondId)

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pondDropdownRef.current && !pondDropdownRef.current.contains(event.target as Node)) {
        setIsPondDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])


  // Fetch records based on pond and record type
  const { data: records, isLoading: isLoadingRecords } = useQuery({
    queryKey: ['trigger-records', farmId, pondId, recordType],
    queryFn: async (): Promise<RecordData[]> => {
      if (!pondId || !recordType) return []

      const params = { limit: 10 }

      switch (recordType) {
        case 'water_quality': {
          const response = await waterQualityApi.list(farmId, pondId, params)
          return (response.data || []) as RecordData[]
        }
        case 'health_check': {
          const response = await fishHealthApi.getPondChecks(farmId, pondId, params)
          return (response.data || []) as RecordData[]
        }
        case 'feed_record': {
          const response = await feedApi.getPondRecords(farmId, pondId, params)
          return (response.data || []) as unknown as RecordData[]
        }
        case 'maintenance_check': {
          const response = await maintenanceApi.getPondChecks(farmId, pondId, params)
          return (response.data || []) as RecordData[]
        }
        default:
          return []
      }
    },
    enabled: !!farmId && !!pondId && !!recordType,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!pondId) return

    onSubmit({
      pondId,
      triggerRecordType: recordType || undefined,
      triggerRecordId: selectedRecordId || undefined,
      description: description || undefined,
      lookbackDays,
    })
  }

  const handlePondSelect = (id: string) => {
    setPondId(id)
    setSelectedRecordId('') // Reset record selection when pond changes
    setIsPondDropdownOpen(false)
  }

  const handleRecordTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRecordType(e.target.value as TriggerRecordType | '')
    setSelectedRecordId('') // Reset record selection when record type changes
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Pond Selection - Custom Dropdown */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Select Pond *
        </label>
        <div className="relative" ref={pondDropdownRef}>
          <button
            type="button"
            onClick={() => !isStreaming && setIsPondDropdownOpen(!isPondDropdownOpen)}
            disabled={isStreaming}
            className={`
              relative w-full rounded-lg border border-slate-200/50 bg-white px-4 py-2.5 text-left shadow-sm
              transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20
              ${isStreaming ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
            `}
          >
            <div className="flex items-center justify-between">
              <span className={`text-sm ${selectedPond ? 'text-gray-900' : 'text-gray-400'}`}>
                {selectedPond ? selectedPond.name : 'Choose a pond...'}
              </span>
              <ChevronDown
                className={`h-5 w-5 text-gray-400 transition-transform ${isPondDropdownOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </button>

          {/* Dropdown Menu */}
          {isPondDropdownOpen && ponds.length > 0 && (
            <div className="absolute z-50 mt-2 w-full rounded-lg border border-slate-200/50 bg-white shadow-lg">
              <div className="max-h-60 overflow-auto py-1">
                {ponds.map((pond) => {
                  const isSelected = pondId === pond.id
                  return (
                    <button
                      key={pond.id}
                      type="button"
                      onClick={() => handlePondSelect(pond.id)}
                      className={`
                        flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors
                        ${isSelected ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'}
                      `}
                    >
                      <div
                        className={`h-4 w-4 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="flex-1 text-left">{pond.name}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Empty state when no ponds */}
          {isPondDropdownOpen && ponds.length === 0 && (
            <div className="absolute z-50 mt-2 w-full rounded-lg border border-slate-200/50 bg-white shadow-lg p-4">
              <p className="text-sm text-gray-500 text-center">No ponds available</p>
            </div>
          )}
        </div>
      </div>

      {/* Record Type Selector */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Reference a Specific Record
        </label>
        <select
          value={recordType}
          onChange={handleRecordTypeChange}
          className="w-full rounded-lg border border-slate-200/50 bg-white px-4 py-2.5 text-sm shadow-sm transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          disabled={isStreaming || !pondId}
        >
          {RECORD_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-slate-500">
          Optional: Point to a specific record to analyze, or leave as "None" for general investigation
        </p>
      </div>

      {/* Record Selection List */}
      {pondId && recordType && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Select {TRIGGER_RECORD_TYPE_LABELS[recordType]}
          </label>
          <TriggerRecordList
            recordType={recordType}
            records={records || []}
            selectedRecordId={selectedRecordId}
            onSelectRecord={setSelectedRecordId}
            isLoading={isLoadingRecords}
            disabled={isStreaming}
          />
        </div>
      )}

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Describe the Problem
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder={
            recordType
              ? "Why does this record concern you? What seems unusual?"
              : "What concerns you about this pond? Describe what you've observed..."
          }
          className="w-full rounded-lg border border-slate-200/50 bg-white px-4 py-2.5 text-sm shadow-sm transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          disabled={isStreaming}
        />
      </div>

      {/* Lookback Days */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Analysis Window (days)
        </label>
        <input
          type="number"
          min={1}
          max={90}
          value={lookbackDays}
          onChange={(e) => setLookbackDays(parseInt(e.target.value) || 30)}
          className="w-32 rounded-lg border border-slate-200/50 bg-white px-4 py-2.5 text-sm shadow-sm transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          disabled={isStreaming}
        />
        <p className="mt-1 text-xs text-slate-500">
          How many days of historical data to analyze (1-90)
        </p>
      </div>

      {/* Submit Button */}
      <div className="flex items-center gap-3 pt-2">
        {isStreaming ? (
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-red-700 transition-colors"
          >
            <StopCircle className="h-4 w-4" />
            Stop Analysis
          </button>
        ) : (
          <button
            type="submit"
            disabled={!pondId}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          >
            <Brain className="h-4 w-4" />
            Analyze Root Cause
          </button>
        )}
      </div>
    </form>
  )
}

export default RootCauseForm
